import { useState, useCallback } from 'react';
import { InvitationTimeline, InvitationSnapshot } from '../../../api/modules/realtimeMessaging/invitationDiagnosticsCache';

interface Props {
  timeline: InvitationTimeline;
  onClose: () => void;
}

const formatTimestamp = (timestamp: number): string => {
  return new Date(timestamp).toLocaleString();
};

const formatDuration = (from: number, to: number): string => {
  const diff = to - from;
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);

  if (hours > 0) return `+${hours}h ${minutes % 60}m`;
  if (minutes > 0) return `+${minutes}m ${seconds % 60}s`;
  return `+${seconds}s`;
};

const getReasonColor = (reason: string) => {
  switch (reason) {
    case 'added':
      return 'bg-green-600';
    case 'updated':
      return 'bg-yellow-600';
    case 'removed':
      return 'bg-red-600';
    default:
      return 'bg-gray-600';
  }
};

const getReasonIcon = (reason: string) => {
  switch (reason) {
    case 'added':
      return '✨';
    case 'updated':
      return '📝';
    case 'removed':
      return '🗑️';
    default:
      return '❓';
  }
};

export const InvitationTimelineScrubber: React.FC<Props> = ({ timeline, onClose }) => {
  const [currentSnapshotIndex, setCurrentSnapshotIndex] = useState(timeline.snapshots.length - 1);
  const [showDiff, setShowDiff] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());

  const currentSnapshot = timeline.snapshots[currentSnapshotIndex];
  const previousSnapshot = currentSnapshotIndex > 0 ? timeline.snapshots[currentSnapshotIndex - 1] : null;

  const goToSnapshot = useCallback(
    (index: number) => {
      setCurrentSnapshotIndex(Math.max(0, Math.min(timeline.snapshots.length - 1, index)));
    },
    [timeline.snapshots.length],
  );

  const nextSnapshot = () => goToSnapshot(currentSnapshotIndex + 1);
  const prevSnapshot = () => goToSnapshot(currentSnapshotIndex - 1);

  const toggleKeySelection = (key: string) => {
    const newSelected = new Set(selectedKeys);
    if (newSelected.has(key)) {
      newSelected.delete(key);
    } else {
      newSelected.add(key);
    }
    setSelectedKeys(newSelected);
  };

  const renderJsonDiff = (current: any, previous: any | null) => {
    if (!previous || !showDiff) {
      return <pre className='overflow-auto rounded bg-neutral-950 p-3 text-xs text-neutral-300'>{JSON.stringify(current, null, 2)}</pre>;
    }

    const findDifferences = (obj1: any, obj2: any, path = ''): Array<{ key: string; current: any; previous: any }> => {
      const diffs: Array<{ key: string; current: any; previous: any }> = [];

      const allKeys = new Set([...Object.keys(obj1 || {}), ...Object.keys(obj2 || {})]);

      for (const key of allKeys) {
        const currentPath = path ? `${path}.${key}` : key;
        const val1 = obj1?.[key];
        const val2 = obj2?.[key];

        if (JSON.stringify(val1) !== JSON.stringify(val2)) {
          diffs.push({ key: currentPath, current: val1, previous: val2 });
        }
      }

      return diffs;
    };

    const differences = findDifferences(current, previous);

    return (
      <div className='space-y-2'>
        {differences.length === 0 ? (
          <p className='text-neutral-400'>No changes detected</p>
        ) : (
          differences.map((diff) => (
            <div key={diff.key} className='rounded border border-neutral-600 bg-neutral-900 p-2'>
              <div className='mb-1 text-xs font-semibold text-blue-400'>{diff.key}</div>
              <div className='grid grid-cols-2 gap-2 text-xs'>
                <div>
                  <div className='mb-1 text-red-400'>Previous:</div>
                  <pre className='overflow-auto rounded bg-red-950 p-1 text-red-200'>{JSON.stringify(diff.previous, null, 2)}</pre>
                </div>
                <div>
                  <div className='mb-1 text-green-400'>Current:</div>
                  <pre className='overflow-auto rounded bg-green-950 p-1 text-green-200'>{JSON.stringify(diff.current, null, 2)}</pre>
                </div>
              </div>
            </div>
          ))
        )}

        {!showDiff && (
          <details className='mt-4'>
            <summary className='cursor-pointer text-xs text-neutral-500 hover:text-neutral-300'>Show Full JSON</summary>
            <pre className='mt-2 overflow-auto rounded bg-neutral-950 p-2 text-xs text-neutral-300'>{JSON.stringify(current, null, 2)}</pre>
          </details>
        )}
      </div>
    );
  };

  return (
    <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/50'>
      <div className='max-h-[90vh] w-[95vw] max-w-6xl overflow-hidden rounded-lg bg-neutral-900 text-neutral-100'>
        {/* Header */}
        <div className='flex items-center justify-between border-b border-neutral-700 p-4'>
          <div>
            <h2 className='text-xl font-semibold'>Timeline: {timeline.invitationId}</h2>
            <p className='text-sm text-neutral-400'>
              {timeline.snapshots.length} snapshot(s) • {formatTimestamp(timeline.firstSeen)} → {formatTimestamp(timeline.lastUpdated)}
            </p>
          </div>
          <button onClick={onClose} className='rounded px-3 py-1 text-neutral-400 hover:bg-neutral-800 hover:text-neutral-200'>
            ✕
          </button>
        </div>

        <div className='flex h-[80vh]'>
          {/* Timeline Sidebar */}
          <div className='w-80 border-r border-neutral-700 bg-neutral-950 p-4'>
            <div className='mb-4 flex items-center justify-between'>
              <h3 className='font-semibold'>Timeline</h3>
              <div className='text-xs text-neutral-400'>
                {currentSnapshotIndex + 1} / {timeline.snapshots.length}
              </div>
            </div>

            {/* Navigation Controls */}
            <div className='mb-4 flex gap-2'>
              <button
                onClick={prevSnapshot}
                disabled={currentSnapshotIndex === 0}
                className='rounded bg-neutral-800 px-2 py-1 text-xs disabled:opacity-50'
              >
                ← Prev
              </button>
              <button
                onClick={nextSnapshot}
                disabled={currentSnapshotIndex === timeline.snapshots.length - 1}
                className='rounded bg-neutral-800 px-2 py-1 text-xs disabled:opacity-50'
              >
                Next →
              </button>
            </div>

            {/* Timeline Scrubber */}
            <div className='mb-4'>
              <input
                type='range'
                min={0}
                max={timeline.snapshots.length - 1}
                value={currentSnapshotIndex}
                onChange={(e) => goToSnapshot(parseInt(e.target.value))}
                className='w-full'
              />
            </div>

            {/* Timeline List */}
            <div className='space-y-1 overflow-auto'>
              {timeline.snapshots.map((snapshot, index) => (
                <button
                  key={snapshot.sequenceId}
                  onClick={() => goToSnapshot(index)}
                  className={`w-full rounded p-2 text-left text-xs transition-colors ${
                    index === currentSnapshotIndex ? 'bg-blue-900 text-blue-200' : 'hover:bg-neutral-800'
                  }`}
                >
                  <div className='flex items-center gap-2'>
                    <span className={`rounded px-1.5 py-0.5 text-xs text-white ${getReasonColor(snapshot.reason)}`}>
                      {getReasonIcon(snapshot.reason)} {snapshot.reason}
                    </span>
                    <span className='text-neutral-400'>#{snapshot.sequenceId}</span>
                  </div>
                  <div className='mt-1 text-neutral-400'>{formatTimestamp(snapshot.timestamp)}</div>
                  {index > 0 && (
                    <div className='text-neutral-500'>{formatDuration(timeline.snapshots[index - 1].timestamp, snapshot.timestamp)}</div>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Content Area */}
          <div className='flex-1 overflow-auto p-4'>
            <div className='mb-4 flex items-center justify-between'>
              <div>
                <h3 className='text-lg font-semibold'>
                  <span className={`mr-2 rounded px-2 py-1 text-sm text-white ${getReasonColor(currentSnapshot.reason)}`}>
                    {getReasonIcon(currentSnapshot.reason)} {currentSnapshot.reason}
                  </span>
                  Snapshot #{currentSnapshot.sequenceId}
                </h3>
                <p className='text-sm text-neutral-400'>{formatTimestamp(currentSnapshot.timestamp)}</p>
              </div>

              <div className='flex gap-2'>
                <button
                  onClick={() => setShowDiff(!showDiff)}
                  disabled={!previousSnapshot}
                  className={`rounded px-3 py-1 text-xs ${
                    showDiff ? 'bg-blue-600 text-white' : 'bg-neutral-800 text-neutral-300 disabled:opacity-50'
                  }`}
                >
                  {showDiff ? 'Hide Diff' : 'Show Diff'}
                </button>
              </div>
            </div>

            {/* Invitation Summary */}
            <div className='mb-4 rounded border border-neutral-700 bg-neutral-800 p-3'>
              <div className='grid grid-cols-2 gap-4 text-sm'>
                <div>
                  <strong>ID:</strong> {currentSnapshot.invitation.id}
                </div>
                <div>
                  <strong>Type:</strong> {currentSnapshot.invitation.type}
                </div>
                <div>
                  <strong>Owner:</strong> {currentSnapshot.invitation.owner?.username || 'Unknown'}
                </div>
                <div>
                  <strong>Invalid:</strong> {currentSnapshot.invitation.invalid ? 'Yes' : 'No'}
                </div>
              </div>
            </div>

            {/* JSON Diff/Content */}
            <div>
              <h4 className='mb-2 font-semibold'>{showDiff && previousSnapshot ? 'Changes from Previous' : 'Full Data'}</h4>
              {renderJsonDiff(currentSnapshot.invitation, showDiff ? previousSnapshot?.invitation : null)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
